/**
 * Permanent project deletion (spec §11B, Phase 9A; structural guard 9E.0).
 *
 * Deliberately destructive and clearly distinct from Archive:
 *  - requires the project's Job Number typed back as confirmation;
 *  - runs as ONE transaction over every project-owned record;
 *  - executes the single authoritative plan in `projectOwnership.ts` — this
 *    service holds NO deletion order of its own, so a new table cannot be
 *    half-covered and the coverage test can check the plan against the
 *    real schema;
 *  - PURCHASES ARE SHARED: a purchase whose allocations all belong to the
 *    deleted project is deleted with its lines/associations; a purchase that
 *    also touches other projects survives with only this project's
 *    allocations removed (normal under-allocation afterwards);
 *  - ATTACHMENT FILES ARE REFERENCE-COUNTED: the SHA-256-deduplicated
 *    physical file is removed only when no association (any project, any
 *    purchase/expense/product/document) references its attachment record any
 *    more — and file bytes are deleted AFTER the transaction commits (files
 *    are not transactional; a failed DB delete must never have destroyed
 *    bytes);
 *  - Job Numbers are never reclaimed (the sequence is untouched).
 */
import type { DbExecutor } from "./executor";
import type { FileStore } from "./fileStore";
import { ValidationError } from "./repositories/projectRepository";
import { PROJECT_DELETION_PLAN, bind, deleteStatement } from "./projectOwnership";

export class ProjectDeletionService {
  constructor(
    private readonly db: DbExecutor,
    private readonly files: FileStore | null,
  ) {}

  /** Summary shown in the confirmation dialog BEFORE deleting. */
  async deletionPreview(projectId: string): Promise<{
    jobNumber: string;
    name: string;
    designs: number;
    features: number;
    lines: number;
    expenses: number;
    allocations: number;
    purchasesToDelete: number;
    purchasesKept: number;
  }> {
    const p = await this.db.select<{ job_number: string; name: string }>(
      "SELECT job_number, name FROM projects WHERE id = ?",
      [projectId],
    );
    if (p.length === 0) throw new ValidationError(["This project no longer exists."]);
    const n = async (sql: string): Promise<number> =>
      Number((await this.db.select<{ n: number }>(sql, [projectId]))[0]?.n ?? 0);
    const touching = await this.touchingPurchaseIds(projectId);
    const doomed = await this.doomedPurchaseIds(projectId, touching);
    return {
      jobNumber: p[0].job_number,
      name: p[0].name,
      designs: await n("SELECT COUNT(*) AS n FROM designs WHERE project_id = ?"),
      features: await n("SELECT COUNT(*) AS n FROM project_features WHERE project_id = ?"),
      lines: await n("SELECT COUNT(*) AS n FROM proposed_cost_lines WHERE project_id = ?"),
      expenses: await n("SELECT COUNT(*) AS n FROM actual_expenses WHERE project_id = ?"),
      allocations: await n(
        "SELECT COUNT(*) AS n FROM purchase_allocations WHERE project_id = ?",
      ),
      purchasesToDelete: doomed.length,
      purchasesKept: touching.length - doomed.length,
    };
  }

  /** Purchases with at least one allocation to this project. */
  private async touchingPurchaseIds(projectId: string): Promise<string[]> {
    const rows = await this.db.select<{ id: string }>(
      `SELECT DISTINCT p.id FROM purchases p
         JOIN purchase_lines l ON l.purchase_id = p.id
         JOIN purchase_allocations a ON a.line_id = l.id
        WHERE a.project_id = ?`,
      [projectId],
    );
    return rows.map((r) => r.id);
  }

  /** Touching purchases whose EVERY allocation belongs to this project. */
  private async doomedPurchaseIds(projectId: string, touching: string[]): Promise<string[]> {
    const doomed: string[] = [];
    for (const id of touching) {
      // Company-source identity outlives individual project attribution.
      if((await this.db.select("SELECT purchase_id FROM company_purchase_details WHERE purchase_id=?",[id])).length)continue;
      const other = await this.db.select<{ n: number }>(
        `SELECT COUNT(*) AS n FROM purchase_allocations a
           JOIN purchase_lines l ON l.id = a.line_id
          WHERE l.purchase_id = ? AND a.project_id <> ?`,
        [id, projectId],
      );
      if (Number(other[0]?.n ?? 0) === 0) doomed.push(id);
    }
    return doomed;
  }

  /**
   * Attachment records this deletion could orphan: those hanging off the
   * project's expenses, its doomed purchases, its documents (an extraction
   * that was never imported has no other association at all) and the reports
   * saved to the project (Phase 13).
   */
  private async orphanCandidates(projectId: string, doomedPurchases: string[]): Promise<Set<string>> {
    const ids = new Set<string>();
    const add = (rows: { attachment_id: string }[]) => rows.forEach((r) => ids.add(r.attachment_id));
    add(await this.db.select<{attachment_id:string}>(`SELECT attachment_id FROM worksheet_issue_attachments99 WHERE issue_id IN (SELECT id FROM worksheet_issues99 WHERE project_id=?)`,[projectId]));
    add(await this.db.select<{attachment_id:string}>(`SELECT attachment_id FROM draw_request_attachments WHERE request_id IN (SELECT id FROM draw_requests WHERE project_id=?)`,[projectId]));
    add(
      await this.db.select<{ attachment_id: string }>(
        `SELECT attachment_id FROM actual_expense_attachments WHERE expense_id IN
           (SELECT id FROM actual_expenses WHERE project_id = ?)`,
        [projectId],
      ),
    );
    add(
      await this.db.select<{ attachment_id: string }>(
        `SELECT attachment_id FROM document_extractions WHERE project_id = ?
           OR imported_purchase_id IN (SELECT l.purchase_id FROM purchase_lines l
              JOIN purchase_allocations a ON a.line_id = l.id WHERE a.project_id = ?)`,
        [projectId, projectId],
      ),
    );
    add(
      await this.db.select<{ attachment_id: string }>(
        "SELECT attachment_id FROM project_attachments WHERE project_id = ?",
        [projectId],
      ),
    );
    // Loan agreements attached to the project's financing facilities and
    // their extraction records (spec §73E) — collected here or the file
    // would outlive the project.
    add(
      await this.db.select<{ attachment_id: string }>(
        `SELECT attachment_id FROM financing_facility_attachments WHERE facility_id IN
           (SELECT id FROM financing_facilities WHERE project_id = ?)`,
        [projectId],
      ),
    );
    add(
      await this.db.select<{ attachment_id: string }>(
        "SELECT attachment_id FROM financing_extractions WHERE project_id = ?",
        [projectId],
      ),
    );
    for (const pid of doomedPurchases) {
      add(
        await this.db.select<{ attachment_id: string }>(
          "SELECT attachment_id FROM purchase_attachments WHERE purchase_id = ?",
          [pid],
        ),
      );
    }
    return ids;
  }

  /**
   * Permanently delete the project. `confirmJobNumber` must match exactly.
   * Returns the number of attachment files physically removed.
   */
  async deleteProject(projectId: string, confirmJobNumber: string): Promise<{ filesRemoved: number }> {
    const rows = await this.db.select<{ job_number: string }>(
      "SELECT job_number FROM projects WHERE id = ?",
      [projectId],
    );
    if (rows.length === 0) throw new ValidationError(["This project no longer exists."]);
    if (rows[0].job_number !== confirmJobNumber.trim()) {
      throw new ValidationError(["The Job Number you typed does not match. Nothing was deleted."]);
    }
    const orphanedFiles: string[] = [];
    await this.db.transaction(async () => {
      const touching = await this.touchingPurchaseIds(projectId);
      const doomedPurchases = await this.doomedPurchaseIds(projectId, touching);
      // Resolved BEFORE anything is deleted: afterwards the associations
      // that identify the candidates are gone.
      const candidateAttachmentIds = await this.orphanCandidates(projectId, doomedPurchases);

      for (const step of PROJECT_DELETION_PLAN) {
        if (step.kind === "delete") {
          await this.db.execute(deleteStatement(step), bind(projectId, step.params));
        } else if (step.kind === "sql") {
          await this.db.execute(step.sql, bind(projectId, step.params));
        } else if (step.id === "doomed-purchases") {
          await this.deleteDoomedPurchases(doomedPurchases);
        } else {
          await this.deleteOrphanedAttachments(candidateAttachmentIds, orphanedFiles);
        }
      }
      // NOTE: job_number_sequences untouched — numbers are never reclaimed.
    });
    // Physical bytes only after the transaction committed.
    let filesRemoved = 0;
    if (this.files) {
      for (const path of orphanedFiles) {
        try {
          await this.files.removeFile(path);
          filesRemoved++;
        } catch (err) {
          console.error("Attachment file could not be removed (record already deleted):", path, err);
        }
      }
    }
    return { filesRemoved };
  }

  /** Purchases whose every allocation belonged to the deleted project. */
  private async deleteDoomedPurchases(doomedPurchases: string[]): Promise<void> {
    for (const pid of doomedPurchases) {
      await this.db.execute("DELETE FROM purchase_attachments WHERE purchase_id = ?", [pid]);
      // Returns referencing a doomed purchase: sever the historical link
      // (the referenced record is being destroyed at the owner's request).
      await this.db.execute("UPDATE purchases SET original_purchase_id = NULL WHERE original_purchase_id = ?", [pid]);
      await this.db.execute(
        "UPDATE purchase_lines SET original_line_id = NULL WHERE original_line_id IN (SELECT id FROM purchase_lines WHERE purchase_id = ?)",
        [pid],
      );
      await this.db.execute("DELETE FROM purchase_lines WHERE purchase_id = ?", [pid]);
      await this.db.execute("DELETE FROM purchases WHERE id = ?", [pid]);
    }
  }

  /** Attachment records nothing references any more (bytes come later). */
  private async deleteOrphanedAttachments(candidates: Set<string>, orphanedFiles: string[]): Promise<void> {
    for (const attachmentId of candidates) {
      const refs = await this.db.select<{ n: number }>(
        `SELECT (SELECT COUNT(*) FROM purchase_attachments WHERE attachment_id = ?)
              + (SELECT COUNT(*) FROM actual_expense_attachments WHERE attachment_id = ?)
              + (SELECT COUNT(*) FROM product_attachments WHERE attachment_id = ?)
              + (SELECT COUNT(*) FROM project_attachments WHERE attachment_id = ?)
              + (SELECT COUNT(*) FROM contractor_quote_attachments WHERE attachment_id = ?)
              + (SELECT COUNT(*) FROM financing_facility_attachments WHERE attachment_id = ?)
              + (SELECT COUNT(*) FROM financing_extractions WHERE attachment_id = ?)
              + (SELECT COUNT(*) FROM document_extractions WHERE attachment_id = ?)
              + (SELECT COUNT(*) FROM draw_request_attachments WHERE attachment_id = ?) + (SELECT COUNT(*) FROM worksheet_issue_attachments99 WHERE attachment_id = ?) AS n`,
        Array(10).fill(attachmentId),
      );
      if (Number(refs[0]?.n ?? 0) !== 0) continue;
      const file = await this.db.select<{ relative_path: string }>(
        "SELECT stored_relative_path AS relative_path FROM attachments WHERE id = ?",
        [attachmentId],
      );
      await this.db.execute("DELETE FROM attachments WHERE id = ?", [attachmentId]);
      if (file[0]) orphanedFiles.push(file[0].relative_path);
    }
  }
}
